import { get } from 'aws-amplify/api';

const FREE_TOKEN_LIMIT = 2000000; // Fallback only; backend returns the real free limit, which resets monthly
const ADMIN_TOKEN_LIMIT = 1000000000; // 1B tokens for admin users

// ============================================
// Types
// ============================================

export interface UsageStatus {
  tokensUsed: number;
  tokenLimit: number;
  isLimitExceeded: boolean;
  remainingTokens: number;
  period: string;
  /** Unix timestamp (seconds) when the user's limit resets (calendar month for free, billing cycle for paid). */
  periodEnd?: number | null;
  tier: string;
  hasSubscription: boolean;
  canSendMessage?: boolean;
  error?: string;
}

// ============================================
// Helper Functions
// ============================================

/**
 * Get today's date in YYYY-MM-DD format (UTC)
 */
function getTodayDate(): string {
  return new Date().toISOString().slice(0, 10);
}

// ============================================
// Public API
// ============================================

/**
 * Get current usage status for a user including limit checking.
 * Calls the backend /mcp/usage-status endpoint which handles:
 * - Checking UserSubscription for tier/limits
 * - Creating MCPUsage record if it doesn't exist
 * - Determining the correct period based on subscription
 *
 * This is now a thin wrapper around the backend API for security
 * (prevents client-side manipulation of usage records).
 */
export async function getUsageStatus(userId: string, isAdmin?: boolean): Promise<UsageStatus> {
  if (!userId) {
    return {
      tokensUsed: 0,
      tokenLimit: FREE_TOKEN_LIMIT,
      isLimitExceeded: false,
      remainingTokens: FREE_TOKEN_LIMIT,
      period: getTodayDate(),
      tier: 'free',
      hasSubscription: false,
      canSendMessage: false,
    };
  }

  try {
    // Note: isAdmin is now extracted server-side from JWT claims (cognito:groups)
    // The isAdmin parameter here is only used for fallback logic if the API call fails
    const restOperation = get({
      apiName: 'mcpProxyApi',
      path: '/mcp/usage-status',
    });

    const response = await restOperation.response;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data = (await response.body.json()) as any;

    const result: UsageStatus = {
      tokensUsed: data.tokensUsed ?? 0,
      tokenLimit: data.tokenLimit ?? FREE_TOKEN_LIMIT,
      isLimitExceeded: data.isLimitExceeded ?? false,
      remainingTokens: data.remainingTokens ?? FREE_TOKEN_LIMIT,
      period: data.period ?? getTodayDate(),
      periodEnd: data.periodEnd ?? null,
      tier: data.tier ?? 'free',
      hasSubscription: data.hasSubscription ?? false,
      canSendMessage: data.canSendMessage ?? true,
      error: data.error,
    };

    return result;
  } catch (error) {
    console.error('[getUsageStatus] Error calling backend:', error);

    // Return a safe fallback that allows usage (fail open for UX)
    // The backend will enforce limits on actual requests
    return {
      tokensUsed: 0,
      tokenLimit: isAdmin ? ADMIN_TOKEN_LIMIT : FREE_TOKEN_LIMIT,
      isLimitExceeded: false,
      remainingTokens: isAdmin ? ADMIN_TOKEN_LIMIT : FREE_TOKEN_LIMIT,
      period: getTodayDate(),
      tier: isAdmin ? 'admin' : 'free',
      hasSubscription: true, // Assume they have access on error
      canSendMessage: true,
      error: error instanceof Error ? error.message : 'Failed to fetch usage status',
    };
  }
}

export type MessageBlockedReason = 'ok' | 'no_subscription' | 'limit_exceeded';

/**
 * Check if user can send a message.
 * Returns the usage status along with the allowed flag and reason if blocked.
 *
 * Note: This is now a read-only check. The backend enforces limits on actual requests.
 */
export async function canSendMessage(userId: string, isAdmin?: boolean): Promise<{
  allowed: boolean;
  usageStatus: UsageStatus;
  reason: MessageBlockedReason;
}> {
  const usageStatus = await getUsageStatus(userId, isAdmin);

  // Check subscription first - no subscription means they need to sign up
  if (!usageStatus.hasSubscription) {
    return {
      allowed: false,
      usageStatus,
      reason: 'no_subscription',
    };
  }

  // Check token limit
  if (usageStatus.isLimitExceeded) {
    return {
      allowed: false,
      usageStatus,
      reason: 'limit_exceeded',
    };
  }

  return {
    allowed: true,
    usageStatus,
    reason: 'ok',
  };
}

/**
 * Get current month's token usage for a user.
 * @deprecated Use getUsageStatus() instead for full usage info including limits.
 */
export async function getCurrentMonthUsage(userId: string): Promise<number> {
  const status = await getUsageStatus(userId);
  return status.tokensUsed;
}
